// Restaurant offering reader. Phase 6A — fetches the per-restaurant
// offering.yaml that drives the subscribe-allocate-form widget's
// numeric inputs (min/max/price). Today the widget reads from
// `subscribe-mocks.ts`; this hook is the dossier-backed replacement.
//
// Single responsibility: fetch ONCE on mount, return {loading, offering, error}.
// Mirrors the shape of `useSubscriptionState` so widgets can compose them
// trivially.

import { useEffect, useState } from 'react';
import { parse as yamlParse } from 'yaml';
import { api } from '@rmint/shell';

export type Offering = {
  rmintId: string;
  restaurantName: string;
  ticker: string;
  tokenContractAddress: `0x${string}`;
  saleContractAddress: `0x${string}`;
  pricePerToken: number;
  minAmountUsdc: number;
  maxAmountUsdc: number;
  lockupMonths: number;
  lockupEndIso: string;
  signByDeadlineIso: string;
  estimatedFirstDistributionIso: string;
  networkFeeUsdcEst: number;
};

export async function fetchOffering(rmintId: string): Promise<Offering | null> {
  const res = await api.get(
    `/dossier/restaurants/${encodeURIComponent(rmintId)}/offering.yaml`,
    { responseType: 'text' },
  );
  if (res.status === 404) return null;
  if (res.status >= 400) {
    throw new Error(`offering read failed: ${res.status}`);
  }
  const text = typeof res.data === 'string' ? res.data : '';
  return yamlParse(text) as Offering;
}

export type OfferingHook = {
  loading: boolean;
  offering: Offering | null;
  error: Error | null;
};

export function useOffering(rmintId: string | undefined): OfferingHook {
  const [loading, setLoading] = useState<boolean>(Boolean(rmintId));
  const [offering, setOffering] = useState<Offering | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!rmintId) {
      setLoading(false);
      setOffering(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await fetchOffering(rmintId);
        if (!cancelled) {
          setOffering(result);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e as Error);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rmintId]);

  return { loading, offering, error };
}
